import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { CalendarRange } from "lucide-react";
import { fmtINR, stageMeta } from "./bizdevUtils";

/** Iter 170 · Roadmap — CSS Gantt of active/launched ideas grouped by venture. */

export default function RoadmapTab({ wsId }) {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/workspaces/${wsId}/bizdev/roadmap`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErrorMessage(e, "Failed to load roadmap")));
  }, [wsId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-8 text-center text-sm text-muted-foreground">Loading roadmap…</div>;

  const from = new Date(data.window.from);
  const to = new Date(data.window.to);
  const span = to - from;
  const pct = (d) => Math.min(Math.max(((new Date(d) - from) / span) * 100, 0), 100);
  const months = [];
  const cur = new Date(from);
  while (cur < to) {
    months.push(new Date(cur));
    cur.setMonth(cur.getMonth() + 1);
  }
  const todayPct = pct(data.today);

  return (
    <div className="space-y-3" data-testid="bizdev-roadmap">
      <div className="text-xs text-muted-foreground">
        Execution timeline for active + launched ideas. Set start/target dates on an idea to pin it — otherwise bars derive from the time horizon.
      </div>
      {data.count === 0 ? (
        <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
          <CalendarRange className="w-6 h-6 mx-auto mb-2" />
          No active ideas to plot yet.
        </div>
      ) : (
        <div className="border border-border bg-card overflow-x-auto">
          <div className="min-w-[900px]">
            {/* Month header */}
            <div className="relative flex border-b border-border bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground">
              <div className="w-56 flex-shrink-0 px-3 py-2 font-semibold">Idea</div>
              <div className="flex-1 relative flex">
                {months.map((m, i) => (
                  <div key={i} className="flex-1 px-1 py-2 border-l border-border/50 whitespace-nowrap overflow-hidden">
                    {m.toLocaleString("en", { month: "short" })} {String(m.getFullYear()).slice(2)}
                  </div>
                ))}
              </div>
            </div>
            {data.groups.map((g) => (
              <div key={g.venture_id || "standalone"} data-testid={`roadmap-group-${g.venture_id || "standalone"}`}>
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold border-b border-border/60"
                     style={{ color: g.color }}>
                  {g.venture_name} · {g.items.length}
                </div>
                {g.items.map((it) => {
                  const left = pct(it.start);
                  const width = Math.max(pct(it.end) - left, 1.5);
                  return (
                    <div key={it.id} className="flex items-center border-b border-border/40 hover:bg-muted/30"
                         data-testid={`roadmap-bar-${it.id}`}>
                      <div className="w-56 flex-shrink-0 px-3 py-2">
                        <div className="text-xs font-medium truncate">{it.title}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {stageMeta(it.stage).label}
                          {it.weighted_monthly > 0 && ` · ${fmtINR(it.weighted_monthly)}/mo`}
                        </div>
                      </div>
                      <div className="flex-1 relative h-9">
                        {/* today line */}
                        <div className="absolute top-0 bottom-0 w-px bg-red-500/70 z-10"
                             style={{ left: `${todayPct}%` }} />
                        <div className="absolute top-2 h-5 rounded-sm flex items-center px-1.5 overflow-hidden"
                             style={{ left: `${left}%`, width: `${width}%`,
                                       background: `${g.color}22`,
                                       borderLeft: `3px solid ${g.color}` }}
                             title={`${it.start} → ${it.end} · confidence ${it.confidence_pct}%`}>
                          <span className="text-[9px] font-mono whitespace-nowrap"
                                style={{ color: g.color }}>
                            {it.start.slice(5)} → {it.end.slice(5)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
